import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import BreadCrumb from '../../../components/breadcrumb/BreadCrumbNavigation';
import Loading from '../../../layouts/loading';
import { getWeeklyGroupedScorecardReport } from '../../../services/reportService';
import { getTimeZone } from '../../../utils/timeCalculation';
import IssueBreakdownDialog from './IssueBreakdownDialog';
import WeekSection from './WeekSection';

const WEEKS_PER_PAGE = 3;

const getLastFriday = () => {
  const d = new Date();
  // Walk backwards until we hit Friday (getDay() === 5)
  while (d.getDay() !== 5) d.setDate(d.getDate() - 1);
  d.setHours(0, 0, 0, 0);
  return d;
};

const getDefaultDateRange = () => ({
  selection: { startDate: getLastFriday(), endDate: new Date(), key: 'selection' },
});

const toDateInputValue = (date) => {
  if (!date) return '';
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const headCellSx = {
  px: 1,
  py: 1,
  fontSize: '0.75rem',
  fontWeight: 700,
  whiteSpace: 'nowrap',
  bgcolor: 'background.paper',
  borderBottom: '2px solid',
  borderColor: 'divider',
  color: '#454f5b',
};

export default function ScorecardTimeline() {
  const [draftDateRange, setDraftDateRange] = useState(getDefaultDateRange);
  const [appliedDateRange, setAppliedDateRange] = useState(getDefaultDateRange);
  const [draftFilter, setDraftFilter] = useState('');
  const [globalFilter, setGlobalFilter] = useState('');
  const [activeSubmissionStatus, setActiveSubmissionStatus] = useState('filled');

  const [loadedGroups, setLoadedGroups] = useState([]);
  const [pageIndex, setPageIndex] = useState(0);
  // Three-state: true = more pages exist, false = exhausted, null = unknown (initial)
  const [hasNext, setHasNext] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);

  const [filledCount, setFilledCount] = useState(0);
  const [notFilledCount, setNotFilledCount] = useState(0);
  const [surveyQuestions, setSurveyQuestions] = useState([]);

  const [issueDialogRow, setIssueDialogRow] = useState(null);
  const issueDialogInvokerRef = useRef(null);
  const sentinelRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Single source of truth for in-flight state — avoids stale-closure races
  // between the IntersectionObserver callback and React state batching.
  const fetchStateRef = useRef({ isFetching: false, pageIndex: 0, hasNext: null });

  const breadcrumbs = useMemo(
    () => [
      { label: 'Home', path: '/' },
      { label: 'Admin', path: '/admin/developer-scorecard' },
      { label: 'Team Scorecard', path: '' },
    ],
    []
  );

  // Debounce globalFilter 300ms
  useEffect(() => {
    const id = setTimeout(() => setGlobalFilter(draftFilter), 300);
    return () => clearTimeout(id);
  }, [draftFilter]);

  const filterKey = useMemo(
    () =>
      JSON.stringify({
        startDate: appliedDateRange.selection.startDate,
        endDate: appliedDateRange.selection.endDate,
        globalFilter,
        activeSubmissionStatus,
      }),
    [appliedDateRange, globalFilter, activeSubmissionStatus]
  );

  const fetchPage = useCallback(
    async (pageIdx) => {
      // Guard: never fire two requests concurrently
      if (fetchStateRef.current.isFetching) return;
      // Guard: never fetch beyond the last page
      if (fetchStateRef.current.hasNext === false) return;

      fetchStateRef.current.isFetching = true;

      abortControllerRef.current = new AbortController();
      const { signal } = abortControllerRef.current;

      if (pageIdx === 0) setIsLoading(true);
      else setIsFetchingMore(true);

      try {
        const response = await getWeeklyGroupedScorecardReport(
          [], [], [], [], [], '',
          appliedDateRange,
          [], [],
          getTimeZone(),
          {
            weekStart: pageIdx,
            weeksPerPage: WEEKS_PER_PAGE,
            globalFilter,
            submissionStatus: activeSubmissionStatus,
          },
          signal
        );

        const newGroups = response?.groups ?? [];
        const nextHasNext = response?.hasNext ?? false;
        const nextPageIdx = pageIdx + 1;

        setLoadedGroups((prev) => [...prev, ...newGroups]);
        setHasNext(nextHasNext);
        setPageIndex(nextPageIdx);

        // Keep ref in sync so the observer can read current values synchronously
        fetchStateRef.current.pageIndex = nextPageIdx;
        fetchStateRef.current.hasNext = nextHasNext;
        fetchStateRef.current.isFetching = false;

        if (response?.surveyQuestions?.length) {
          setSurveyQuestions((prev) => (prev.length ? prev : response.surveyQuestions));
        }
        if (pageIdx === 0) {
          setFilledCount(response?.filledCount ?? 0);
          setNotFilledCount(response?.notFilledCount ?? 0);
          if (response?.surveyQuestions?.length) {
            setSurveyQuestions(response.surveyQuestions);
          }
        }

        setIsLoading(false);
        setIsFetchingMore(false);

        // If this page had no visible groups but more exist, auto-advance
        if (newGroups.length === 0 && nextHasNext) {
          fetchPage(nextPageIdx);
        }
      } catch (err) {
        if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
        fetchStateRef.current.isFetching = false;
        setIsLoading(false);
        setIsFetchingMore(false);
        toast.error(err?.response?.data?.message || 'Unable to fetch scorecard.');
      }
    },
    // fetchPage only changes when the active filter values change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [appliedDateRange, globalFilter, activeSubmissionStatus]
  );

  // Reset all state + fire first fetch whenever any filter changes
  useEffect(() => {
    abortControllerRef.current?.abort();
    setLoadedGroups([]);
    setPageIndex(0);
    setHasNext(null);
    setSurveyQuestions([]);
    fetchStateRef.current = { isFetching: false, pageIndex: 0, hasNext: null };
    fetchPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  // IntersectionObserver — fires only when sentinel enters viewport AND more pages exist
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (
          entry.isIntersecting &&
          fetchStateRef.current.hasNext === true &&
          !fetchStateRef.current.isFetching
        ) {
          fetchPage(fetchStateRef.current.pageIndex);
        }
      },
      { rootMargin: '100px' }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
    // Re-create observer only when fetchPage identity changes (i.e. filter change)
  }, [fetchPage]);

  const applyDateRange = () => {
    const { startDate, endDate } = draftDateRange.selection;
    if (!startDate || !endDate) {
      toast.error('Please select both start and end dates.');
      return;
    }
    if (new Date(startDate) > new Date(endDate)) {
      toast.error('Start date cannot be after end date.');
      return;
    }
    setAppliedDateRange(draftDateRange);
  };

  const dateRangeLabel = useMemo(() => {
    try {
      const { startDate, endDate } = appliedDateRange?.selection || {};
      if (!startDate || !endDate) return '';
      return `${new Date(startDate).toLocaleDateString()} – ${new Date(endDate).toLocaleDateString()}`;
    } catch {
      return '';
    }
  }, [appliedDateRange]);

  const q1Header = surveyQuestions?.[0]?.questionText || 'Question 1';
  const q2Header = surveyQuestions?.[1]?.questionText || 'Question 2';

  const isEmpty = !isLoading && loadedGroups.length === 0 && hasNext === false;

  return (
    <>
      <BreadCrumb breadcrumbs={breadcrumbs} />
      <Container maxWidth="4xl" sx={{ pb: 6 }}>
        <Loading open={isLoading} />

        {/* Filter card */}
        <Card
          sx={{
            p: 2,
            mb: 2,
            border: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="center" justifyContent="space-between">
            {/* Submission status tabs */}
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {[
                { value: 'filled', label: 'Filled', count: filledCount },
                { value: 'not_filled', label: 'Not Filled', count: notFilledCount },
              ].map((option) => (
                <Chip
                  key={option.value}
                  label={`${option.label} (${option.count})`}
                  color={activeSubmissionStatus === option.value ? 'primary' : 'default'}
                  variant={activeSubmissionStatus === option.value ? 'filled' : 'outlined'}
                  onClick={() => setActiveSubmissionStatus(option.value)}
                />
              ))}
            </Stack>

            {/* Date range + search */}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
              <TextField
                label="Start Date"
                type="date"
                size="small"
                value={toDateInputValue(draftDateRange.selection.startDate)}
                onChange={(e) =>
                  setDraftDateRange((prev) => ({
                    selection: {
                      ...prev.selection,
                      startDate: e.target.value ? new Date(e.target.value) : null,
                    },
                  }))
                }
                InputLabelProps={{ shrink: true, style: { fontSize: '0.75rem' } }}
                inputProps={{ style: { fontSize: '0.75rem' } }}
              />
              <TextField
                label="End Date"
                type="date"
                size="small"
                value={toDateInputValue(draftDateRange.selection.endDate)}
                onChange={(e) =>
                  setDraftDateRange((prev) => ({
                    selection: {
                      ...prev.selection,
                      endDate: e.target.value ? new Date(e.target.value) : null,
                    },
                  }))
                }
                InputLabelProps={{ shrink: true, style: { fontSize: '0.75rem' } }}
                inputProps={{ style: { fontSize: '0.75rem', padding: '8.5px 9px' } }}
              />
              <Button variant="contained" onClick={applyDateRange} sx={{ fontSize: '0.75rem' }}>
                Apply
              </Button>
              <TextField
                label="Search by name / email"
                size="small"
                value={draftFilter}
                onChange={(e) => setDraftFilter(e.target.value)}
                sx={{ minWidth: 220 }}
                InputLabelProps={{ style: { fontSize: '0.75rem' } }}
                inputProps={{ style: { fontSize: '0.75rem', padding: '8.5px 9px' } }}
              />
            </Stack>
          </Stack>
        </Card>

        {/* Table */}
        <Paper variant="outlined">
          <TableContainer sx={{ maxHeight: 640 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headCellSx}>Name</TableCell>
                  <TableCell sx={headCellSx}>Task Info</TableCell>
                  <TableCell sx={headCellSx}>Planned hrs</TableCell>
                  <TableCell sx={headCellSx}>Productive hrs</TableCell>
                  <TableCell sx={headCellSx}>Productivity %</TableCell>
                  <TableCell sx={headCellSx}>Issues</TableCell>
                  <TableCell sx={{ ...headCellSx, minWidth: 220 }}>{q1Header}</TableCell>
                  <TableCell sx={{ ...headCellSx, minWidth: 220 }}>{q2Header}</TableCell>
                  <TableCell sx={headCellSx}>Submitted At</TableCell>
                  <TableCell sx={headCellSx}>Survey Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadedGroups.map((group) => (
                  <WeekSection
                    key={group.periodKey ?? 'not-submitted'}
                    periodKey={group.periodKey}
                    weekLabel={group.weekLabel}
                    weekStart={group.weekStart}
                    weekEnd={group.weekEnd}
                    users={group.users}
                    surveyQuestions={surveyQuestions}
                    onIssueClick={(user) => setIssueDialogRow(user)}
                  />
                ))}

                {isEmpty && (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                      <Typography color="text.secondary">
                        No scorecard data for this period.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}

                {isFetchingMore && (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 2, border: 0 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                )}

                {/* End-of-data indicator */}
                {!isLoading && !isFetchingMore && hasNext === false && loadedGroups.length > 0 && (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 1.5, border: 0 }}>
                      <Typography variant="caption" color="text.disabled">
                        All weeks loaded
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Sentinel lives inside Paper but outside TableContainer so it scrolls with the page */}
          <Box ref={sentinelRef} sx={{ height: 1 }} />
        </Paper>
      </Container>

      <IssueBreakdownDialog
        open={Boolean(issueDialogRow)}
        onClose={() => {
          setIssueDialogRow(null);
          queueMicrotask(() => issueDialogInvokerRef.current?.focus?.());
        }}
        developerName={issueDialogRow?.userName || ''}
        dateRangeLabel={dateRangeLabel}
        totalIssues={issueDialogRow?.quality?.totalIssues ?? 0}
        issuesByCategory={issueDialogRow?.quality?.issuesByCategory ?? {}}
      />
    </>
  );
}
